// src/components/admin/AssignBills.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Search, CheckCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { agentsAPI, adminAPI } from '../../services/api';
import { getDisplayName } from '../../utils/displayName';
import { toast } from 'react-toastify';

/** API may send `collection_unit` (backend DTO) or `collectionUnit` (camelCase JSON elsewhere). */
function rawCollectionUnit(unit) {
  const v = unit?.collection_unit ?? unit?.collectionUnit;
  if (v === null || v === undefined) return undefined;
  return v;
}

function collectionUnitDisplay(unit) {
  const v = rawCollectionUnit(unit);
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

/** Stable string id for API / selection (matches DB collection_unit). */
function collectionUnitKey(unit) {
  const v = rawCollectionUnit(unit);
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

const AssignBills = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [collectionUnits, setCollectionUnits] = useState([]);
  const [selectedCollectionUnits, setSelectedCollectionUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [agentsData, cuData] = await Promise.all([
          agentsAPI.getAll().catch(() => null),
          adminAPI.getCollectionUnits().catch(() => null),
        ]);
        if (cancelled) return;
        setAgents(Array.isArray(agentsData) ? agentsData : []);
        const list = Array.isArray(cuData)
          ? cuData
          : cuData?.data ?? cuData?.collectionUnits ?? cuData?.items ?? [];
        setCollectionUnits(Array.isArray(list) ? list : []);
      } catch (error) {
        if (!cancelled) {
          toast.error('Failed to load assign screen data');
          setAgents([]);
          setCollectionUnits([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchCollectionUnits = useCallback(async () => {
    try {
      const data = await adminAPI.getCollectionUnits();
      const list = Array.isArray(data)
        ? data
        : data?.data ?? data?.collectionUnits ?? data?.items ?? [];
      setCollectionUnits(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error('Failed to load collection units');
      setCollectionUnits([]);
    }
  }, []);

  useEffect(() => {
    if (!selectedAgent?.id) return;
    fetchCollectionUnits();
  }, [selectedAgent?.id, fetchCollectionUnits]);

  const filteredAgents = agents.filter((agent) => {
    const agentName = (getDisplayName(agent) || '').toLowerCase();
    const agentPhone = (agent.user?.phone || agent.phone || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    return agentName.includes(q) || agentPhone.includes(q);
  });

  /** Units already assigned to any agent are not selectable. */
  const isAssigned = (unit) => {
    const assignedTo = unit.assigned_to ?? unit.assignedTo;
    return Boolean(assignedTo);
  };

  const otherAgentLabel = (unit) => {
    const ag = unit.assigned_agent ?? unit.assignedAgent;
    const name = (ag?.name || '').trim();
    if (name) return name;
    if (ag?.email) return ag.email;
    return 'another agent';
  };

  /** Digit-only query matches collection_unit only. Otherwise match unit label (+ strip words like unit/cu) and bill counts/amounts. */
  const unitMatchesCollectionSearch = (unit, rawQ) => {
    const trimmed = rawQ.trim();
    const q = trimmed.toLowerCase();
    if (!q) return true;
    const cuStr = collectionUnitDisplay(unit).toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    const cuDigits = cuStr.replace(/\D/g, '');
    const digitsOnlyQuery = /^\d+$/.test(trimmed);
    if (digitsOnlyQuery) {
      return qDigits.length > 0 && (cuDigits === qDigits || cuDigits.startsWith(qDigits));
    }
    if (cuStr.includes(q)) return true;
    if (qDigits.length > 0 && cuDigits.includes(qDigits)) return true;
    const qNoLabel = q
      .replace(/\bcollection\s*unit\b/g, '')
      .replace(/\bunit\b/g, '')
      .replace(/\bcu\b/g, '')
      .trim();
    if (qNoLabel && cuStr.includes(qNoLabel)) return true;
    const hay = [
      cuStr,
      String(unit.bill_count ?? unit.billCount ?? ''),
      String(unit.total_owed ?? unit.totalOwed ?? ''),
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  };

  const filteredCollectionUnits = collectionUnits
    .filter((unit) => unitMatchesCollectionSearch(unit, unitSearchQuery))
    .sort((a, b) =>
      collectionUnitKey(a).localeCompare(collectionUnitKey(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setSelectedCollectionUnits([]);
    setUnitSearchQuery('');
  };

  const toggleCollectionUnit = (unitId, checked) => {
    if (checked) setSelectedCollectionUnits((prev) => [...prev, unitId]);
    else setSelectedCollectionUnits((prev) => prev.filter((u) => u !== unitId));
  };

  const handleAssignClick = () => {
    if (!selectedAgent) return toast.error('Please select an agent first');
    if (selectedCollectionUnits.length === 0) return toast.error('Please select at least one collection unit');
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    try {
      setAssigning(true);
      await adminAPI.assignCollectionUnits(selectedAgent.id, selectedCollectionUnits);
      toast.success(
        `Assigned collection units (${selectedCollectionUnits.join(', ')}) to ${getDisplayName(selectedAgent) || 'Agent'}`,
      );
      setShowConfirmDialog(false);
      setSelectedAgent(null);
      setSelectedCollectionUnits([]);
      setUnitSearchQuery('');
      await fetchCollectionUnits();
    } catch (error) {
      toast.error('Failed to assign collection units');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className='h-full flex flex-col'>
      <header className='p-4 sm:p-6 bg-[#001E3C] border-b border-white/10'>
        <div className='flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6'>
          <button
            type='button'
            onClick={() => navigate('/admin/dashboard')}
            className='glass-card p-2.5 sm:p-3 rounded-xl active:scale-95 transition'>
            <ChevronLeft className='w-5 h-5 text-white' />
          </button>
          <h2 className='text-lg sm:text-xl font-black uppercase text-white'>Assign Collection Units</h2>
        </div>

        {!selectedAgent ? (
          <div className='relative'>
            <Search className='absolute left-4 top-3.5 w-5 h-5 text-white/30 pointer-events-none' />
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder:text-white/40'
              placeholder='Search agents…'
            />
          </div>
        ) : (
          <div className='relative'>
            <Search className='absolute left-4 top-3.5 w-5 h-5 text-white/30 pointer-events-none' />
            <input
              type='search'
              value={unitSearchQuery}
              onChange={(e) => setUnitSearchQuery(e.target.value)}
              className='w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder:text-white/40 focus:border-[#00F5FF]/50'
              placeholder='Search by collection unit # (e.g. 12 or unit 12)…'
              autoComplete='off'
            />
          </div>
        )}
      </header>

      <main className='flex-1 overflow-y-auto p-4 sm:p-6 no-scrollbar'>
        {loading ? (
          <div className='text-center py-12'>
            <p className='text-white/60'>Loading...</p>
          </div>
        ) : (
          <div className='space-y-6 max-w-6xl mx-auto'>
            {!selectedAgent && (
              <div className='space-y-4'>
                <h3 className='text-base sm:text-lg font-bold text-white'>Select an Agent</h3>
                {filteredAgents.map((agent) => (
                  <div
                    key={agent.id}
                    onClick={() => handleAgentSelect(agent)}
                    className='glass-card p-4 border-l-4 border-[#00F5FF] cursor-pointer active:scale-95 transition-transform'>
                    <p className='font-bold text-base sm:text-lg text-white'>
                      {getDisplayName(agent) || 'Unknown Agent'}
                    </p>
                    {(agent.user?.phone || agent.phone) && (
                      <p className='text-sm text-white/60'>{agent.user?.phone || agent.phone}</p>
                    )}
                  </div>
                ))}
                {filteredAgents.length === 0 && (
                  <p className='text-center text-white/50 text-sm'>No agents found</p>
                )}
              </div>
            )}

            {selectedAgent && (
              <div className='space-y-4'>
                <div className='glass-card p-4 border-l-4 border-[#00E676]'>
                  <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                    <div>
                      <h3 className='text-base sm:text-lg font-bold text-white'>
                        Assigning to: {getDisplayName(selectedAgent) || 'Agent'}
                      </h3>
                      <p className='text-sm text-white/60'>Select collection units</p>
                    </div>
                    <div className='flex flex-wrap gap-3 sm:justify-end'>
                      <button
                        type='button'
                        onClick={() => fetchCollectionUnits()}
                        className='text-sm glass-card px-3 py-1.5 rounded-lg text-white/90 hover:text-white flex items-center gap-1.5'>
                        <RefreshCw className='w-3.5 h-3.5' />
                        Refresh units
                      </button>
                      <button
                        type='button'
                        onClick={() => {
                          setSelectedAgent(null);
                          setSelectedCollectionUnits([]);
                          setUnitSearchQuery('');
                        }}
                        className='text-sm text-white/70 hover:text-white underline text-left sm:text-right'>
                        Change Agent
                      </button>
                    </div>
                  </div>
                </div>

                {collectionUnits.length > 0 && (
                  <p className='text-xs text-white/50 px-1'>
                    {unitSearchQuery.trim()
                      ? `Showing ${filteredCollectionUnits.length} unit${filteredCollectionUnits.length !== 1 ? 's' : ''} matching search (of ${collectionUnits.length} total)`
                      : `${collectionUnits.length} collection unit${collectionUnits.length !== 1 ? 's' : ''} in database — ${collectionUnits.filter((u) => !isAssigned(u)).length} available to assign to this agent`}
                  </p>
                )}

                <div className='rounded-xl border border-white/10 overflow-hidden bg-white/[0.02]'>
                  {collectionUnits.length === 0 ? (
                    <div className='text-center py-12'>
                      <CheckCircle className='w-14 h-14 text-[#00E676] mx-auto mb-4' />
                      <p className='text-lg font-bold text-white'>No collection units found</p>
                    </div>
                  ) : filteredCollectionUnits.length === 0 ? (
                    <div className='text-center py-10 px-4'>
                      <p className='text-white/70 text-sm'>
                        No units match &quot;{unitSearchQuery.trim()}&quot;. Try another search.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className='grid grid-cols-[2.5rem_minmax(0,1fr)_5rem_7rem] gap-2 sm:gap-3 items-center px-3 py-2.5 bg-white/5 border-b border-white/10 text-[10px] font-bold uppercase tracking-wider text-white/45'>
                        <span className='w-[2.5rem] shrink-0' aria-hidden='true' />
                        <span className='min-w-0'>Unit ID (collection_unit)</span>
                        <span className='text-right tabular-nums'>Bills</span>
                        <span className='text-right tabular-nums'>Total owed</span>
                      </div>
                      <ul className='divide-y divide-white/10'>
                        {filteredCollectionUnits.map((unit) => {
                          const display = collectionUnitDisplay(unit);
                          const unitId = collectionUnitKey(unit);
                          const locked = isAssigned(unit);
                          const isSelected =
                            unitId !== "" && selectedCollectionUnits.includes(unitId);
                          const billCount = Number(unit.bill_count ?? unit.billCount ?? 0);
                          const totalOwed = Number(unit.total_owed ?? unit.totalOwed ?? 0);
                          return (
                            <li key={`${collectionUnitKey(unit)}-${display}`}>
                              <label
                                className={`grid grid-cols-[2.5rem_minmax(0,1fr)_5rem_7rem] gap-2 sm:gap-3 items-center px-3 py-3 transition-colors ${
                                  locked
                                    ? 'opacity-75 cursor-not-allowed bg-white/[0.02]'
                                    : `cursor-pointer ${isSelected ? 'bg-[#00F5FF]/10' : 'hover:bg-white/[0.04]'}`
                                }`}>
                                <input
                                  type='checkbox'
                                  className='h-5 w-5 justify-self-center cursor-pointer accent-[#00F5FF] disabled:cursor-not-allowed disabled:opacity-40'
                                  checked={isSelected}
                                  disabled={!unitId || locked}
                                  onChange={(e) => {
                                    if (locked) return;
                                    toggleCollectionUnit(unitId, e.target.checked);
                                  }}
                                  aria-label={
                                    locked
                                      ? `Collection unit ${display} is already assigned to ${otherAgentLabel(unit)}`
                                      : `Select collection unit ${display}`
                                  }
                                />
                                <span className='min-w-0 flex flex-col gap-0.5'>
                                  <span
                                    className={`text-sm font-bold truncate ${
                                      isSelected ? 'text-[#00F5FF]' : 'text-white'
                                    }`}
                                    title={
                                      display
                                        ? `Property collection_unit field: ${display}`
                                        : 'Missing collection_unit on this summary row'
                                    }>
                                    {display || '—'}
                                  </span>
                                  {locked && (
                                    <span className='text-[10px] text-amber-300/90 font-medium truncate'>
                                      Assigned to {otherAgentLabel(unit)}
                                    </span>
                                  )}
                                </span>
                                <span className='text-sm text-white/90 tabular-nums text-right'>{billCount}</span>
                                <span className='text-sm font-bold text-[#FFC107] tabular-nums text-right'>
                                GHC{totalOwed.toLocaleString()}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {selectedAgent && (
        <footer className='p-4 sm:p-6 border-t border-white/10'>
          <button
            type='button'
            onClick={handleAssignClick}
            disabled={selectedCollectionUnits.length === 0 || assigning}
            className='w-full py-3 sm:py-4 rounded-2xl btn-gold font-black uppercase text-xs sm:text-sm'
            style={{ opacity: selectedCollectionUnits.length === 0 ? 0.5 : 1 }}>
            {assigning
              ? 'Assigning...'
              : `Assign ${selectedCollectionUnits.length} Collection Unit${
                  selectedCollectionUnits.length !== 1 ? 's' : ''
                }`}
          </button>
        </footer>
      )}

      {showConfirmDialog && (
        <div className='fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4'>
          <div className='bg-[#001E3C] rounded-2xl p-5 sm:p-6 w-full max-w-md border border-white/10'>
            <h3 className='text-lg sm:text-xl font-black text-white mb-4'>Confirm Assignment</h3>
            <p className='text-white/80 mb-6 text-sm sm:text-base'>
              Assign collection units ({selectedCollectionUnits.join(', ')}) to{' '}
              {getDisplayName(selectedAgent) || 'Agent'}?
            </p>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirmDialog(false)}
                disabled={assigning}
                className='py-3 rounded-xl glass-card text-white font-bold'>
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirm}
                disabled={assigning}
                className='py-3 rounded-xl btn-gold font-black uppercase'>
                {assigning ? 'Assigning...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignBills;
